import { useCallback, useMemo } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { HashIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import TagModalChooser, {
  TagChooserContent,
} from "components/lib/Choosers/Tags/TagModalChooser";

import type { IdeaDetail, UpdateIdeaFn } from "../types";

type IdeaTagsProps = {
  updateIdea: UpdateIdeaFn;
  idea: IdeaDetail;
  isSubmission?: boolean;
  noMargin?: boolean;
};

const IdeaTags = ({
  updateIdea,
  idea,
  isSubmission = false,
  noMargin = false,
}: IdeaTagsProps) => {
  const user = useCurrentUser();
  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [idea?.challenge], {
      isEnabled: !!idea?.challenge,
    }),
  );
  const challenge = challengeQuery.data;

  const currentChallenge = challenge || idea.ownerChallenge;

  const { t } = useTranslation();
  const canEdit = useMemo(
    () =>
      util.canEditIdea(user, idea) ||
      util.canManageChallenge(user, currentChallenge) ||
      util.hasPermission(user, "challenge.editIdeaTags", currentChallenge?._id),
    [user, idea, currentChallenge],
  );

  const updateTagListWithTag = useCallback(
    (tag: OpenAPI.Schemas["Tag"]) => {
      const tagList = [...(currentChallenge?.tagList ?? [])];
      const index = tagList.findIndex((t) => t._id === tag._id);
      if (index >= 0) {
        tagList[index] = tag;
      }

      if (challenge) {
        api.setQueryData(
          api.challenges.get,
          [challenge._id],
          (prevChallenge) => ({ ...(prevChallenge || challenge), tagList }),
          { allowUndefined: true },
        );
      } else if (currentChallenge) {
        updateIdea({ ...currentChallenge, tagList }, ["ownerChallenge"], true);
      }
    },
    [updateIdea, currentChallenge, challenge],
  );

  const addTag = useCallback(
    (tag: Pick<OpenAPI.Schemas["Tag"], "_id">) => {
      api.tags.tagSingle(
        "ideas",
        tag._id,
        idea._id,
        (updatedTag) => {
          updateIdea(
            [
              ...(idea.ownerTags || []).filter((t) => t._id !== updatedTag._id),
              updatedTag,
            ],
            ["ownerTags"],
            true,
          );
          updateIdea(
            [
              ...(idea.tags || []).filter((t: string) => t !== updatedTag._id),
              updatedTag._id,
            ],
            ["tags"],
            true,
          );
          updateTagListWithTag(updatedTag);
        },
        (err) => toast.error(err.message),
      );
    },
    [idea, updateIdea, updateTagListWithTag],
  );

  const removeTag = useCallback(
    (tag: Pick<OpenAPI.Schemas["Tag"], "_id">) => {
      api.tags.untagSingle(
        "ideas",
        tag._id,
        idea._id,
        (updatedTag) => {
          updateIdea(
            [...(idea.ownerTags || []).filter((t) => t._id !== updatedTag._id)],
            ["ownerTags"],
            true,
          );
          updateIdea(
            [...(idea.tags || []).filter((t) => t !== updatedTag._id)],
            ["tags"],
            true,
          );
          updateTagListWithTag(updatedTag);
        },
        (err) => toast.error(err.message),
      );
    },
    [idea, updateIdea, updateTagListWithTag],
  );

  if (
    !currentChallenge ||
    !currentChallenge.tagList ||
    currentChallenge.tagList.length === 0
  ) {
    return null;
  }
  if (isSubmission) {
    return (
      <TagChooserContent
        tags={idea.ownerTags ?? []}
        allTags={currentChallenge.tagList.filter(
          (tag) => !tag?.disabledEntities?.includes("ideas"),
        )}
        removeTag={removeTag}
        addTag={addTag}
        showCount={true}
      />
    );
  }

  return (
    <TagModalChooser
      trigger={
        <Button size="small" variant="outline">
          <Icon icon={HashIcon} />
          {t("tags.edit")}
        </Button>
      }
      tags={idea.ownerTags ?? []}
      availableTags={currentChallenge.tagList.filter(
        (tag) => !tag?.disabledEntities?.includes("ideas"),
      )}
      addTag={addTag}
      removeTag={removeTag}
      canEdit={canEdit}
      noMargin={noMargin}
    />
  );
};

export default IdeaTags;
